import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { UsuarioService } from '../services/auth/usuario.service';
import { CustomMessageService } from '../services/utils/message.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard {
  constructor(
    private router: Router,
    private usuarioService: UsuarioService,
    private messageService: CustomMessageService
  ) {}

  /**
   * Determina si una ruta puede activarse según los roles del usuario.
   *
   * Propósito:
   * - Centralizar la autorización por rol para rutas protegidas.
   *
   * Flujo:
   * 1. Verifica vigencia de sesión/token.
   * 2. Obtiene el usuario actual desde `localStorage`.
   * 3. Evalúa roles permitidos de la ruta más `ROLE_ADMIN`.
   * 4. Si no cumple, redirige al inicio y notifica acceso denegado.
   *
   * @param {ActivatedRouteSnapshot} route Ruta objetivo con metadata de roles.
   * @param {RouterStateSnapshot} state Estado de navegación (no usado en la lógica actual).
   * @returns {boolean} `true` si el usuario está autorizado; `false` en caso contrario.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    this.usuarioService.verificarCaducudadToken();

    let usuario = JSON.parse(localStorage.getItem('usuario') || null);

    if (this.tieneRol(usuario, [...route.data['roles'], 'ROLE_ADMIN'])) {
      return true;
    } else {
      this.router.navigateByUrl('/');
      this.messageService.alert('You are not authorized to access this section', 'Access denied');
      return false;
    }
  }

  /**
   * Verifica si el usuario posee al menos uno de los roles solicitados.
   *
   * Propósito:
   * - Reutilizar la validación de roles en reglas de acceso generales y específicas.
   *
   * Flujo:
   * 1. Valida que exista usuario y colección de roles.
   * 2. Recorre los roles del usuario.
   * 3. Compara cada `role.name` contra la lista objetivo.
   *
   * @param {any} usuario Usuario autenticado con su colección de roles.
   * @param {string[]} roles Conjunto de roles válidos para la operación.
   * @returns {boolean} `true` cuando encuentra coincidencia; `false` si no existe.
   */
  tieneRol(usuario: any, roles: string[]): boolean {
    return usuario && usuario?.roles?.find((role: any) => roles.includes(role.name));
  }
}
